import { useState } from "react";
import { Search, Instagram } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/community";
import { useAdminData, adminAct, NSelect, inputCls, Badge, Panel, btnDark, btnDanger, btnLine } from "./shared";

export function ApplicationsTab() {
  const [status, setStatus] = useState("pending");
  const [items, reload] = useAdminData(`/admin/community/applications${status ? `?status=${status}` : ""}`, [status]);
  const decide = async (a, decision) => {
    const note = window.prompt(`${decision === "approved" ? "Approve" : "Decline"} ${a.first_name}? Optional personal note for her email:`, "");
    if (note === null) return;
    await adminAct(() => api.post(`/admin/community/applications/${a.id}/decision`, { decision, note: note || null }), decision === "approved" ? "Approved — welcome email sent" : "Declined — email sent");
    reload();
  };
  return (
    <Panel title="Community applications" testId="admin-applications-tab"
      action={<NSelect value={status} onChange={setStatus} className="w-44" testId="apps-status-filter"
        options={[{ value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Declined" }, { value: "", label: "All" }]} />}>
      <div className="grid md:grid-cols-2 gap-4">
        {(items || []).map((a) => (
          <div key={a.id} className="border border-[#E8DCCB] rounded-lg p-5" data-testid={`app-card-${a.id}`}>
            <div className="flex justify-between gap-3">
              <div>
                <p className="font-serif text-xl">{a.first_name} {a.last_name}</p>
                <p className="text-xs text-[#7A6A62]">{a.email} · {a.city}, {a.state} · {fmtDate(a.created_at)}</p>
                {a.instagram && <a href={`https://instagram.com/${a.instagram}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 mt-1 underline"><Instagram className="w-3 h-3" />@{a.instagram}</a>}
              </div>
              <Badge s={a.status} />
            </div>
            <p className="text-sm mt-4 text-[#4A403B] whitespace-pre-line">{a.introduction}</p>
            {a.interests?.length > 0 && <p className="text-xs mt-3"><span className="text-[#7A6A62]">Interests:</span> {a.interests.join(", ")}</p>}
            {a.activities?.length > 0 && <p className="text-xs mt-1"><span className="text-[#7A6A62]">Activities:</span> {a.activities.join(", ")}</p>}
            {a.status === "pending" && (
              <div className="flex gap-2 mt-5">
                <button className={btnDark} onClick={() => decide(a, "approved")} data-testid={`app-approve-${a.id}`}>Approve</button>
                <button className={btnDanger} onClick={() => decide(a, "rejected")} data-testid={`app-reject-${a.id}`}>Decline</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {items && !items.length && <p className="text-sm text-[#7A6A62] py-6">No applications here.</p>}
    </Panel>
  );
}

const C_OPTS = ["none", "pending", "approved", "rejected", "suspended"];
const P_OPTS = ["none", "pending", "approved", "suspended", "revoked", "rejected"];

export function MembersTab() {
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [cs, setCs] = useState("");
  const params = new URLSearchParams({ ...(query ? { q: query } : {}), ...(cs ? { community_status: cs } : {}) }).toString();
  const [items, reload] = useAdminData(`/admin/community/members${params ? `?${params}` : ""}`, [params]);
  const update = (m, patch) => adminAct(() => api.put(`/admin/community/members/${m.user_id}`, patch), "Member updated").then(reload);

  return (
    <Panel title="Members" testId="admin-members-tab"
      action={
        <form onSubmit={(e) => { e.preventDefault(); setQuery(q); }} className="flex gap-2">
          <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#7A6A62]" />
            <input className={`${inputCls} pl-8 w-56`} placeholder="Search name, email, city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="members-search-input" /></div>
          <NSelect value={cs} onChange={setCs} className="w-40" testId="members-status-filter" options={[{ value: "", label: "All statuses" }, ...C_OPTS.map((o) => ({ value: o, label: o }))]} />
          <button className={btnLine} data-testid="members-search-btn">Search</button>
        </form>
      }>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#7A6A62]"><th className="py-2">Member</th><th>Location</th><th>Community</th><th>Privé</th><th>RSVPs</th><th>Joined</th></tr></thead>
          <tbody>
            {(items || []).map((m) => (
              <tr key={m.user_id} className="border-t border-[#F0E8DD]" data-testid={`member-row-${m.user_id}`}>
                <td className="py-3"><p>{m.name || "—"}</p><p className="text-xs text-[#7A6A62]">{m.email}</p></td>
                <td className="text-xs">{[m.city, m.state].filter(Boolean).join(", ")}</td>
                <td><NSelect value={m.community_status || "none"} onChange={(v) => update(m, { community_status: v })} options={C_OPTS} className="w-32 !py-1" testId={`member-community-${m.user_id}`} /></td>
                <td><NSelect value={m.prive_status || "none"} onChange={(v) => update(m, { prive_status: v })} options={P_OPTS} className="w-32 !py-1" testId={`member-prive-${m.user_id}`} /></td>
                <td>{m.rsvp_count}</td>
                <td className="text-xs text-[#7A6A62]">{fmtDate(m.created_at, { month: "short", day: "numeric", year: "numeric" })}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items && !items.length && <p className="text-sm text-[#7A6A62] py-6">No members found.</p>}
      </div>
    </Panel>
  );
}
